import React, { useState } from 'react';
import { ArrowUpRight, Mail, Phone, Shield, X, Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { submitContact } from '../../lib/api';

const INTEREST_OPTIONS = [
  'Content Syndication',
  'SQL & BANT Leads',
  'Programmatic Display',
  'LinkedIn & Social',
  'Appointment Setting',
  'Webinars & Events',
  'Predictor.com Platform',
  'Something else',
];

function ContactModal({ open, onClose, prefilledEmail }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: prefilledEmail || '',
    company: '',
    phone: '',
    interest: INTEREST_OPTIONS[0],
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    if (open && prefilledEmail) setForm((f) => ({ ...f, email: prefilledEmail }));
  }, [open, prefilledEmail]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes('@')) {
      toast({ title: 'Missing details', description: 'Name and a valid email are required.' });
      return;
    }
    setLoading(true);
    try {
      await submitContact({ ...form, source: 'landing_cta' });
      setSuccess(true);
      toast({
        title: "Thanks — we'll be in touch.",
        description: `A strategist will contact ${form.email} within 24h.`,
      });
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setForm({ name: '', email: '', company: '', phone: '', interest: INTEREST_OPTIONS[0], message: '' });
      }, 1600);
    } catch (err) {
      toast({
        title: 'Could not submit',
        description: err?.response?.data?.detail || 'Please try again in a moment.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#050A1F]/85 backdrop-blur-md animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="contact-modal-backdrop"
    >
      <div
        className="relative w-full max-w-lg bg-[#0F1C40] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-modal"
      >
        <div
          className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.35), transparent 70%)' }}
        />
        <div
          className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.25), transparent 70%)' }}
        />

        <div className="relative p-6 lg:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-2">
                Book a strategy call
              </div>
              <h3 className="text-2xl lg:text-3xl font-serif italic text-[#F1EDE4] leading-tight">
                Tell us where you want your pipeline in 90 days.
              </h3>
            </div>
            <button
              onClick={onClose}
              data-testid="contact-modal-close"
              className="text-white/50 hover:text-white/90 shrink-0 -mr-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="py-10 flex flex-col items-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-[#4ECDC4]/20 border border-[#4ECDC4]/40 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#4ECDC4]" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-lg font-serif italic text-[#F1EDE4]">Message received.</div>
                <div className="text-sm text-white/60 mt-1">A strategist will reach out within 24h.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">Name*</label>
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Jane Doe"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F1EDE4] placeholder:text-white/30 focus:outline-none focus:border-[#4ECDC4]"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">Work email*</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="you@company.com"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F1EDE4] placeholder:text-white/30 focus:outline-none focus:border-[#4ECDC4]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">Company</label>
                  <input
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={update('company')}
                    placeholder="Acme Inc."
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F1EDE4] placeholder:text-white/30 focus:outline-none focus:border-[#4ECDC4]"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">Phone</label>
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="+1 (555) 000-0000"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F1EDE4] placeholder:text-white/30 focus:outline-none focus:border-[#4ECDC4]"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">
                  I&apos;m interested in
                </label>
                <select
                  data-testid="contact-select-interest"
                  value={form.interest}
                  onChange={update('interest')}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F1EDE4] focus:outline-none focus:border-[#4ECDC4]"
                >
                  {INTEREST_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-[#0F1C40]">
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 block">
                  What are you trying to solve?
                </label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update('message')}
                  rows={3}
                  placeholder="A quick line on ICP, volume target, or timeline…"
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F1EDE4] placeholder:text-white/30 focus:outline-none focus:border-[#4ECDC4] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#4ECDC4] text-[#0F1C40] py-3 rounded-full text-sm font-semibold hover:bg-[#FFB020] transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Book a strategy call <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </>
                )}
              </button>
              <p className="text-[10px] text-white/40 text-center">
                By submitting, you agree to our{' '}
                <a href="/privacy" className="text-[#4ECDC4] underline">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CTA() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Enter a valid email', description: 'We need a way to reach you.' });
      return;
    }
    setModalOpen(true);
  };

  return (
    <section id="cta" className="py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-white/50 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#4ECDC4] pulse-dot" />
              <span>Let&apos;s Talk</span>
            </div>
            <h2 className="text-6xl sm:text-7xl lg:text-9xl xl:text-[160px] tracking-[-0.04em] leading-[0.9] font-medium">
              Let&apos;s build
              <br />
              your <span className="font-serif italic font-normal text-[#4ECDC4]">pipeline.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-neutral-400 text-lg leading-relaxed">
              Built for B2B. Powered by AI. Talk to an EvolveBPM strategist and get a demand-gen plan tuned to your ICP and pipeline goals.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="cta-quick-form">
              <input
                data-testid="cta-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@company.com"
                className="flex-1 bg-transparent border-b border-neutral-700 py-4 px-1 text-lg text-[#F1EDE4] placeholder:text-neutral-600 focus:outline-none focus:border-[#4ECDC4] transition-colors"
              />
              <button
                type="submit"
                data-testid="cta-open-modal-button"
                className="inline-flex items-center gap-2 bg-[#4ECDC4] text-[#0F1C40] pl-6 pr-5 py-4 rounded-full text-sm font-semibold hover:bg-[#FFB020] transition-colors"
              >
                Book a strategy call
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </form>

            <p className="mt-4 text-xs text-white/50">
              By submitting, you agree to our{' '}
              <a href="/privacy" className="text-[#4ECDC4] underline">
                Privacy Policy
              </a>
              . We&apos;ll route your enquiry to{' '}
              <a className="text-[#4ECDC4]" href="mailto:Satish.k@evolvebpm.com">
                Satish.k@evolvebpm.com
              </a>
              .
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <a href="mailto:Satish.k@evolvebpm.com" className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#4ECDC4]/50 transition group">
                <Mail className="w-5 h-5 text-[#4ECDC4] mb-3" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">General enquiries</div>
                <div className="text-sm font-medium">Satish.k@evolvebpm.com</div>
              </a>
              <a href="mailto:DPO@evolvebpm.com" className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#4ECDC4]/50 transition group">
                <Shield className="w-5 h-5 text-[#FFB020] mb-3" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">GDPR / Data Protection</div>
                <div className="text-sm font-medium">DPO@evolvebpm.com</div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#12234C] border border-white/10 rounded-2xl p-6 lg:p-7">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-5">
                <Phone className="w-3.5 h-3.5" />
                <span>Reach us by phone</span>
              </div>
              <div className="space-y-4">
                {[
                  { region: 'United States', flag: '🇺🇸', tel: '+1 201 379 6001', href: 'tel:+12013796001' },
                  { region: 'United Kingdom', flag: '🇬🇧', tel: '+44 7463 5393', href: 'tel:+4474635393' },
                  { region: 'India', flag: '🇮🇳', tel: '+91 9686596333', href: 'tel:+919686596333' },
                ].map((r) => (
                  <a key={r.region} href={r.href} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0 hover:text-[#4ECDC4] transition-colors group">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">{r.region}</div>
                      <div className="text-lg font-medium">{r.tel}</div>
                    </div>
                    <span className="text-2xl">{r.flag}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} prefilledEmail={email} />
    </section>
  );
}
